import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Routes, Route } from "react-router-dom";
import FreelancerServices from "./components/FreelancerServices";
import FreelancerExperience from "./components/FreelancerExperience";
import FreelancerPhotos from "./components/FreelancerPhotos";
import FreelancerReviews from "./components/FreelancerReviews";
import ContactFormOverlay from "../../../components/messaging/ContactFormOverlay";
import { useAuth } from "../../../context/AuthContext";
import { createContactForm } from "../../../services/messagingService";
import { toast } from "react-toastify";
import ContactDropdown from "../../../components/ui/ContactDropdown";
import { getImage } from "../../../utils/cloudinarySizing";

const FreelancerPublicProfile = ({ defaultTab = "Services", outletContext }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();

    const [isOverlayOpen, setOverlayOpen] = useState(false);
    const [sliderIndex, setSliderIndex] = useState(0);

    const [activeComponent, setActiveComponent] = useState(defaultTab);
    const [isMessagePopupOpen, setMessagePopupOpen] = useState(false);
    const [isFavoritedEffect, setIsFavoritedEffect] = useState(false);
    const [recentlyUnfavorited, setRecentlyUnfavorited] = useState(false);
    const [isContactOpen, setIsContactOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);


    const { role, accessToken } = useAuth();

    const canFavorite = role === "user";

    // Pull data passed from FreelancerProfile
    const {
        freelancerData,
        popupMessage,
        isSubscribed,
        isFavorited,
        favoriteCount,
        subscribersCount,
        reviews,
        handleFavorite,
        handleSubscribe,
    } = outletContext;


    const tabs = [
        { name: "Services", icon: "fa-solid fa-wrench" },
        { name: "Experience", icon: "fa-solid fa-hourglass-half" },
        // Renamed from "Media" to "Photos" and simplified structure
        { name: "Images", icon: "fa-solid fa-image" },
        { name: "Reviews", icon: "fa-solid fa-star", style: { color: "#ffcc00" } },
    ];

    const toggleContactMenu = () => {
        setIsContactOpen(prev => !prev);
    };

    const handleSendContactForm = () => {
        if (!accessToken || !role) {
            toast.info("Login or Sign Up to send a contact form");
            return;
        }
        setIsContactOpen(false);
        setMessagePopupOpen(true); // reuse your existing popup
    };

    useEffect(() => {
        const current = location.pathname.split("/").pop();
        const match = tabs.findIndex(tab => tab.name.toLowerCase() === current.toLowerCase());
        if (match !== -1) {
            setActiveComponent(tabs[match].name);
            setSliderIndex(match);
        } else {
            // If no match (base route), default to Services
            setActiveComponent("Services");
            setSliderIndex(0);
        }
    }, [location.pathname]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 900);
        handleResize(); // initialize
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const renderComponent = () => {
        switch (activeComponent) {
            case "Services": return <FreelancerServices freelancerId={freelancerData?.profile_id} />;
            case "Experience": return <FreelancerExperience freelancerData={freelancerData} />;
            case "Images": return <FreelancerPhotos freelancerId={freelancerData?.id} />;
            case "Reviews": return <FreelancerReviews freelancerId={freelancerData?.id} />;
            default: return null;
        }
    };

    const renderTabButtons = () => (
        tabs.map((tab, index) => (
            <button
                key={tab.name}
                className={`nav-button ${activeComponent === tab.name ? "active" : ""}`}
                onClick={() => handleTabClick(tab.name, index)}
            >
                {tab.icon && <i className={tab.icon} style={tab.style}></i>}
                <span style={{ marginLeft: "8px" }}>
                    {/* Removed conditional check for "Media" to simplify rendering */}
                    {tab.name}
                </span>
            </button>
        ))
    );

    const handleTabClick = (tabName, index) => {
        setActiveComponent(tabName);
        setSliderIndex(index);
        navigate(`/freelancer/${id}/${tabName.toLowerCase()}`);
    };

    const handleOverlayToggle = () => {
        setOverlayOpen(!isOverlayOpen);
    };

    const handleTogglePopup = () => {
        setMessagePopupOpen(!isMessagePopupOpen);
    };

    // Calculate average rating
    const calculateAverageRating = (reviews) => {
        if (reviews.length === 0) return 0; // Avoid division by zero
        const totalRating = reviews.reduce((acc, review) => acc + review.rating, 0);
        const avg = totalRating / reviews.length;
        return Number.isInteger(avg) ? avg : avg.toFixed(1);
    };

    const formatLocation = (city, state) => {
        if (!city && !state) return null;
        if (city && state) return ` ${city}, ${state}`;
        return city || state;
    };

    const formatPhone = (value) => {
        const digits = value.replace(/\D/g, "").substring(0, 10);
        const part1 = digits.substring(0, 3);
        const part2 = digits.substring(3, 6);
        const part3 = digits.substring(6, 10);

        if (digits.length > 6) return `(${part1}) ${part2}-${part3}`;
        if (digits.length > 3) return `(${part1}) ${part2}`;
        if (digits.length > 0) return `(${part1}`;
        return "";
    };

    const averageRating = calculateAverageRating(reviews);

    const ContactButton = (
        <ContactDropdown
            isContactOpen={isContactOpen}
            toggleContactMenu={toggleContactMenu}
            freelancerData={freelancerData}
            isMobile={isMobile}
            handleSendContactForm={handleSendContactForm}
        />
    );

    return (
        <div className="freelancer-page-wrapper">
            <div className="freelancer-profile-container">
                <div className="centered-content">
                    <div className="freelancer-profile-content">
                        {/* Left: Profile photo */}
                        <div className="photo-and-name">
                            <div className="freelancer-profile-photo" onClick={handleOverlayToggle}>
                                <img src={getImage(freelancerData?.photo_original, "avatar_sm") || '/servspace-placeholder-500x500.png'} alt="Profile" />
                            </div>

                            <div className="name-icons-wrapper">
                                <div className="name-row">
                                    <h3 className="freelancer-name">
                                        {`${freelancerData?.first_name || ""} ${freelancerData?.last_name || ""}`.trim() || "Freelancer"}
                                    </h3>
                                    <span className="freelancer-handle">@{freelancerData?.username}</span>
                                </div>



                                <div className="freelancer-icons">


                                    {!isMobile && ContactButton}

                                    {/* Location group */}
                                    {(freelancerData?.city || freelancerData?.state) && (
                                        <div className="freelancer-location">
                                            <i className="fa-solid fa-location-dot"></i>
                                            <span>
                                                {formatLocation(freelancerData?.city, freelancerData?.state)}
                                            </span>
                                        </div>
                                    )}

                                    {/* Divider — only show if location AND at least one link exists */}
                                    {(freelancerData?.city || freelancerData?.state) &&
                                        (freelancerData?.portfolio_link ||
                                            freelancerData?.facebook ||
                                            freelancerData?.instagram ||
                                            freelancerData?.x ||
                                            freelancerData?.youtube) && (
                                            <span className="freelancer-divider">|</span>
                                        )}

                                    {isMobile && (
                                        <div className="contact-button-mobile">
                                            {ContactButton}
                                        </div>
                                    )}


                                    {!isMobile && (
                                        <div className="freelancer-links-row">
                                            {freelancerData?.portfolio_link && (
                                                <a
                                                    href={freelancerData.portfolio_link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="freelancer-portfolio-icon"
                                                    title="Website"
                                                >
                                                    <i className="fa-solid fa-link"></i>
                                                </a>
                                            )}

                                            {freelancerData?.facebook && (
                                                <a
                                                    href={freelancerData?.facebook}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="freelancer-portfolio-icon"
                                                    title="Facebook"
                                                >
                                                    <i className="fa-brands fa-facebook"></i>
                                                </a>
                                            )}

                                            {freelancerData?.instagram && (
                                                <a
                                                    href={freelancerData?.instagram}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="freelancer-portfolio-icon"
                                                    title="Instagram"
                                                >
                                                    <i className="fa-brands fa-instagram"></i>
                                                </a>
                                            )}

                                            {freelancerData?.x && (
                                                <a
                                                    href={freelancerData?.x}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="freelancer-portfolio-icon"
                                                    title="X"
                                                >
                                                    <i className="fa-brands fa-x-twitter"></i>
                                                </a>
                                            )}

                                            {freelancerData?.youtube && (
                                                <a
                                                    href={freelancerData?.youtube}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="freelancer-portfolio-icon"
                                                    title="YouTube"
                                                >
                                                    <i className="fa-brands fa-youtube"></i>
                                                </a>
                                            )}
                                        </div>
                                    )}

                                </div>
                            </div>

                            {isOverlayOpen && (
                                <div className="image-overlay" onClick={handleOverlayToggle}>
                                    {freelancerData ? (
                                        <img src={freelancerData?.photo_original || '/servspace-placeholder-500x500.png'} alt="Enlarged Profile" />
                                    ) : (
                                        <p>Loading...</p>
                                    )}
                                </div>
                            )}
                        </div>

                        {isMobile ? (
                            // Mobile: wrap together
                            <div className="stats-heart-wrapper-mobile">
                                {/* Stats */}


                                {/* Heart button */}
                                <div className="heart-button-container">
                                    <button
                                        className={`save-button 
                    ${isFavorited ? "favorited" : "unfavorited"} 
                    ${!canFavorite ? "disabled" : ""}`}
                                        onClick={
                                            canFavorite
                                                ? () => handleFavorite(freelancerData?.id, freelancerData?.role)
                                                : undefined
                                        }
                                        disabled={!canFavorite}
                                        title={!canFavorite ? "Only users can favorite freelancers" : "Favorite"}
                                    >
                                        <i className="fa-solid fa-heart" />
                                        {favoriteCount > 0 && (
                                            <span className="favorite-count">{favoriteCount}</span>
                                        )}
                                    </button>
                                </div>
                                <div className="stats-container">
                                    <div className="stat-item">
                                        <p><i className="fa-solid fa-star"></i> {averageRating}</p>
                                        <h4>Rating</h4>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            // Desktop: keep separate
                            <>
                                <div className="stats-container">
                                    <div className="stat-item">
                                        <p><i className="fa-solid fa-star"></i> {averageRating}</p>
                                        <h4>Rating</h4>
                                    </div>
                                </div>

                                <div className="heart-button-container">
                                    <button
                                        className={`save-button 
                    ${isFavorited ? "favorited" : "unfavorited"} 
                    ${!canFavorite ? "disabled" : ""}`}
                                        onClick={
                                            canFavorite
                                                ? () => handleFavorite(freelancerData?.id, freelancerData?.role)
                                                : undefined
                                        }
                                        disabled={!canFavorite}
                                        title={!canFavorite ? "Only users can favorite freelancers" : "Favorite"}
                                    >
                                        <i className="fa-solid fa-heart" />
                                        {favoriteCount > 0 && (
                                            <span className="favorite-count">{favoriteCount}</span>
                                        )}
                                    </button>
                                </div>
                            </>
                        )}

                    </div>
                    <div className="container">

                        <div className="button-row">
                            {/* Sliding Bar */}
                            <div
                                className="freelancer-slider"
                                style={{
                                    width: `calc(100% / ${tabs.length})`,
                                    transform: `translateX(${sliderIndex * 100}%)`,
                                }}
                            />
                            {renderTabButtons()}
                        </div>
                        {renderComponent()}
                    </div>
                </div>

                <ContactFormOverlay
                    isOpen={isMessagePopupOpen}
                    onClose={() => setMessagePopupOpen(false)}
                    id={freelancerData?.profile?.id}
                    role={freelancerData?.role}
                />
            </div>
        </div>

    );
};

export default FreelancerPublicProfile;
